'use client';

import { Divider, Tab } from '@bgzt/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import QS from 'query-string';

import { BUTTON_ID, EVENT_ACTION, EVENT_TYPE, PAGE_ID } from '~/apis/log/constant';
import { Footer, LogClick, LogProductViewContent, LogVisit, ScrollToTopButton, Spacing } from '~/components';
import { Link } from '~/components/Link';
import { useEnvironmentInfo } from '~/hooks';
import { goToFullWebviewOnApp } from '~/services/app-interface';
import { authStorage } from '~/services/auth';
import { isNumeric } from '~/types';
import { urlSearchParamsToObject } from '~/utils';

import {
  Info,
  Overview,
  Guide,
  PaymentActionButton,
  ShopReviews,
  ShopReviewsSummary,
} from '~/app/products/[pid]/_components';

import { productDetailOptions, shopReviewsOptions } from '~/app/products/[pid]/_queries';

import styles from './ProductDetail.module.scss';

const DISPLAY_NAME = 'ProductDetail';

const TABS = [
  { value: 'info', label: '상품정보' },
  { value: 'review', label: '후기' },
  { value: 'guide', label: '구매가이드' },
] as const;

const tabGuard = (tab: unknown): tab is (typeof TABS)[number]['value'] => TABS.some((t) => t.value === tab);

type Props = {
  pid: string;
};

export const ProductDetail = ({ pid }: Props) => {
  const { isWebview } = useEnvironmentInfo();
  const searchParams = useSearchParams();

  const { data } = useQuery(productDetailOptions({ pid, uid: authStorage.getUid() }));

  const { data: shopReviewsData } = useInfiniteQuery(
    shopReviewsOptions(data?.shop.uid as number, { page: 0, size: 10 }, { enabled: !!data?.shop.uid }),
  );

  const tab = searchParams.get('tab');
  const validTab = tabGuard(tab) ? tab : 'info';

  const tabSectionRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    tabSectionRef.current?.scrollIntoView({ block: 'start' });
  }, [validTab]);

  const renderTotalReviewCount = (totalElements?: number) => {
    if (!totalElements) return '';
    if (totalElements > 9_999) return '9,999+';
    return totalElements.toLocaleString();
  };

  if (!isNumeric(pid) || !data) return null;

  return (
    <>
      <LogProductViewContent data={data} />

      <LogVisit
        params={{
          event_type: EVENT_TYPE.VISIT,
          event_action: EVENT_ACTION.VISIT,
          page_id: PAGE_ID.PRODUCT,
          page_label: data.product.pid.toString(),
        }}
      />

      <Overview data={data} />

      <Divider thickness={10} />

      <Tab value={validTab} defaultValue="info">
        <div ref={tabSectionRef}>
          <Tab.List type="fixed" className={clsx(styles.stickyTab, isWebview && styles.hasAppBar)}>
            {TABS.map((tab) => (
              <LogClick
                key={tab.value}
                params={{
                  event_type: EVENT_TYPE.SELECT,
                  event_action: EVENT_ACTION.SELECT_BUTTON,
                  button_id: BUTTON_ID.PRODUCT_TAB[tab.value],
                  page_label: pid,
                  url: goToFullWebviewOnApp({
                    url: `${globalThis?.window?.location.origin}/products/${pid}?tab=${tab.value}`,
                  }).to,
                }}
              >
                <Tab.Trigger key={tab.value} value={tab.value}>
                  <Link
                    href={QS.stringifyUrl({
                      url: '',
                      query: {
                        ...urlSearchParamsToObject(searchParams),
                        tab: tab.value,
                      },
                    })}
                    scroll={false}
                    replace
                  >
                    {tab.label}{' '}
                    {tab.value === 'review' && renderTotalReviewCount(shopReviewsData?.pages[0].data.totalElements)}
                  </Link>
                </Tab.Trigger>
              </LogClick>
            ))}
          </Tab.List>
        </div>

        <Tab.Content value={'info'} className={styles.wrapper}>
          <Info product={data?.product} shop={data?.shop} />
        </Tab.Content>
        <Tab.Content value={'review'} className={styles.wrapper}>
          <ShopReviewsSummary shopUid={data.shop.uid} />
          <Spacing height={10} className={styles.shopReviewsSpacing} />
          <ShopReviews shopUid={data.shop.uid} />
        </Tab.Content>
        <Tab.Content value={'guide'} className={styles.wrapper}>
          <Guide />
        </Tab.Content>
      </Tab>

      <ScrollToTopButton bottom={128} />
      <PaymentActionButton product={data.product} shop={data.shop} />
      <Footer shopId={data.shop.shopId} />
    </>
  );
};

ProductDetail.displayName = DISPLAY_NAME;
